import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { EventVoucherActionBar } from '../event-voucher-action-bar';
import { CareBenefitOverrideDialog } from './care-benefit-override-dialog';
import { CareBenefitOverridePanel } from './care-benefit-override-panel';
import { useBenefitOverrides } from '@/lib/disruptions/use-benefit-overrides';
import type { BenefitOverrideApplicationRow } from '@/lib/disruptions/types';
import { CareCardsTools } from './care-cards-tools';
import { CareCounterBar } from './care-counter-bar';
import { CareRedeemByReference } from './care-redeem-by-reference';
import { CareGoalBar } from './care-goal-bar';
import { CareWorkQueue } from './care-work-queue';
import { useCareDesk } from './use-care-desk';

// Disruption "Care" tab (feat/care-desk K5+K6, NEX-457/NEX-458) — remainder-first
// CARE DESK, the meal/care sibling of the Hotels desk: the goal bar + the counter
// capture bar + the work queue (every entitled PNR still owed a live card,
// grouped by why) lead the view; the disruption-wide mass bar (re-homed here from
// the Hotels tab) sits above the sub-tabs; "Cards & tools" (K6) holds the money
// tiles + the cards table. An all-settled desk lands on Cards & tools (the queue
// would be an empty room — the event-allocation-tab precedent). A roster deep
// link (`#care?pnr=…`) or a counter-bar pick PINS one row into the resolve panel
// — even a row in no open lane (read-only facts). Contained scroll only (§18.1):
// the page never scrolls — each pane owns its own overflow. Realtime = WS
// (roster invalidations + the `CareChanged` board merge), never a poll.

// flight-delay U3 — regulatory compensation proposals (immutable why, counter edit,
// airline approve / reject). Lazy: its own chunk, fetched when the desk renders.
const CompensationPanel = lazy(() =>
  import('@/components/compensation/compensation-panel').then((m) => ({ default: m.CompensationPanel })),
);

type DeskMode = 'queue' | 'tools';

export function CareDeskTab({
  disruptionUrn,
  enabled,
  focusPnr,
}: {
  disruptionUrn: string;
  /** LAZY: the tab's queries only run while the Care tab is active (CostTab pattern). */
  enabled: boolean;
  /** Deep link (`#care?pnr=<subCaseUrn>`): pre-select that row in the resolve panel. */
  focusPnr?: string | undefined;
}) {
  const { t } = useTranslation('care');
  const deskQuery = useCareDesk(disruptionUrn, enabled);
  const desk = deskQuery.desk;
  const [mode, setMode] = useState<DeskMode>('queue');

  // #819 — event-level benefit override: "Elevar monto" dialog + the
  // Autorizaciones panel; per-PNR result chips come from the LATEST applied
  // override that touched each group (the record is the source — the roster
  // denorm carries no override link).
  const [proposing, setProposing] = useState(false);
  const [focusOverride, setFocusOverride] = useState<string | null>(null);
  const overrides = useBenefitOverrides(disruptionUrn, enabled);
  const overrideByGroup = useMemo(() => {
    const map = new Map<string, { overrideUrn: string; row: BenefitOverrideApplicationRow }>();
    for (const o of [...(Array.isArray(overrides.data) ? overrides.data : [])].reverse()) {
      if (o.status !== 'APPLIED') continue;
      for (const r of o.application ?? []) map.set(r.subCaseUrn, { overrideUrn: o.urn, row: r });
    }
    return map;
  }, [overrides.data]);

  // PINNED selection (counter bar / roster deep link): may target a row in NO
  // open lane — the queue's resolve panel then renders read-only facts.
  const [pinnedUrn, setPinnedUrn] = useState<string | null>(focusPnr ?? null);
  useEffect(() => {
    if (focusPnr) {
      setPinnedUrn(focusPnr);
      setMode('queue');
    }
  }, [focusPnr]);

  const pinned = useMemo(
    () => (pinnedUrn ? (desk?.rows.find((d) => d.row.subCaseUrn === pinnedUrn) ?? null) : null),
    [pinnedUrn, desk],
  );
  // Drop a stale pin once a LOADED roster no longer carries the row (never while
  // the walk is still empty/loading — the deep link must survive the first render).
  useEffect(() => {
    if (pinnedUrn && desk && desk.rows.length > 0 && !pinned) setPinnedUrn(null);
  }, [pinnedUrn, desk, pinned]);

  // All-settled auto-land: the queue would be an empty room — open on Cards &
  // tools instead (event-allocation-tab precedent). A pinned row (deep link /
  // counter pick) is explicit intent for the resolve panel, so it wins.
  const effectiveMode: DeskMode = mode === 'queue' && desk?.allSettled && !pinned ? 'tools' : mode;

  // LOADING — skeletons mirroring the loaded frame (goal bar → counter bar →
  // mass bar → tabs → queue grid), so nothing jumps when the data lands.
  if (deskQuery.isLoading || (!desk && !deskQuery.isError)) {
    return (
      <div
        className="flex h-full min-h-0 flex-col gap-3 p-3 sm:p-4"
        data-testid="care-desk-loading"
      >
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-7 w-56" />
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[minmax(15rem,2fr)_minmax(0,3fr)]">
          <Skeleton className="h-full min-h-24 w-full" />
          <Skeleton className="hidden h-full w-full lg:block" />
        </div>
      </div>
    );
  }

  // ERROR — the roster walk failed; the failure is actionable.
  if (deskQuery.isError || !desk) {
    return (
      <div className="flex h-full min-h-0 flex-col p-3 sm:p-4" data-testid="care-desk-error">
        <EmptyState
          title={t('tab.error.title')}
          description={t('tab.error.body')}
          action={<Button onClick={() => deskQuery.refetch()}>{t('actions.retry')}</Button>}
        />
      </div>
    );
  }

  const coldRoster = desk.rows.length === 0;
  const nobodyEntitled = !coldRoster && desk.goalPnrs === 0;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3 sm:p-4" data-testid="care-desk-tab">
      <CareGoalBar desk={desk} truncated={deskQuery.truncated} />

      {/* Counter capture bar (K6) — the walk-up fast path, pinned under the goal
          bar; `/` or ⌘K focuses it, Enter pins the row into the resolve panel. */}
      {!coldRoster ? (
        <CareCounterBar
          rows={desk.rows}
          onSelect={(urn) => {
            setPinnedUrn(urn);
            setMode('queue');
          }}
        />
      ) : null}

      {/* #808 — counter redemption by PRINTED reference (caso 2: paper coupon). */}
      {!coldRoster ? (
        <CareRedeemByReference
          disruptionUrn={disruptionUrn}
          caseUrnOf={(sub) => desk.rows.find((d) => d.row.subCaseUrn === sub)?.row.caseUrn}
          onSelect={(urn) => {
            setPinnedUrn(urn);
            setMode('queue');
          }}
        />
      ) : null}

      {/* The disruption-wide mass bar — re-homed from the Hotels tab (K5). */}
      <EventVoucherActionBar disruptionUrn={disruptionUrn} />

      {/* #819 — propose → authorize (another person) → apply, policy untouched. */}
      <CareBenefitOverridePanel
        disruptionUrn={disruptionUrn}
        focusUrn={focusOverride}
        onOpenProposal={() => setProposing(true)}
      />
      {proposing ? (
        <CareBenefitOverrideDialog
          disruptionUrn={disruptionUrn}
          rows={desk.rows.map((d) => d.row)}
          onClose={() => setProposing(false)}
        />
      ) : null}

      {/* flight-delay U3 — the regulatory amount owed for a delay, as a proposal the
          airline approves (APPROVED = recorded liability, nothing is paid). */}
      <Suspense fallback={<Skeleton className="h-10 w-full" data-testid="compensation-panel-loading" />}>
        <CompensationPanel disruptionUrn={disruptionUrn} enabled={enabled} />
      </Suspense>

      <div className="flex items-center gap-1" role="tablist" aria-label={t('tab.ariaLabel')}>
        <ModeTab
          active={effectiveMode === 'queue'}
          onClick={() => setMode('queue')}
          testId="care-mode-queue"
        >
          {t('tab.workQueue')}{' '}
          {desk.remainderPnrs > 0 ? (
            <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold tabular-nums text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {desk.remainderPnrs}
            </span>
          ) : null}
        </ModeTab>
        <ModeTab
          active={effectiveMode === 'tools'}
          onClick={() => setMode('tools')}
          testId="care-mode-tools"
        >
          {t('tab.cardsTools')}
        </ModeTab>
      </div>

      {effectiveMode === 'queue' ? (
        // Differentiated empties: a COLD roster (fan-out still running) is not the
        // same story as an entitled-free disruption or a worked-to-zero queue.
        coldRoster ? (
          <EmptyState
            title={t('tab.empty.coldRoster.title')}
            description={t('tab.empty.coldRoster.body')}
          />
        ) : nobodyEntitled ? (
          <EmptyState
            title={t('tab.empty.notEntitled.title')}
            description={t('tab.empty.notEntitled.body')}
          />
        ) : desk.queue.length === 0 && desk.blocked.length === 0 && !pinned ? (
          <EmptyState
            title={t('tab.empty.queueClear.title')}
            description={t('tab.empty.queueClear.body')}
          />
        ) : (
          <CareWorkQueue
            queue={desk.queue}
            blocked={desk.blocked}
            disruptionUrn={disruptionUrn}
            board={deskQuery.board}
            pinned={pinned}
            onClearPinned={() => setPinnedUrn(null)}
            overrideByGroup={overrideByGroup}
            onOpenOverride={(urn) => setFocusOverride(urn)}
          />
        )
      ) : (
        <CareCardsTools
          desk={desk}
          board={deskQuery.board}
          boardUpdatedAt={deskQuery.boardUpdatedAt}
          disruptionUrn={disruptionUrn}
          onGoToQueue={() => setMode('queue')}
          onRetry={() => deskQuery.refetch()}
        />
      )}
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'rounded-md px-3 py-1 text-sm transition-colors',
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
